import React, { useState } from 'react';
import threadService from '../../services/threadService';
import styles from './ForumThreadModerationActions.module.css';

const ForumThreadModerationActions = ({
  thread,
  currentForumId,
  forumOptions = [],
  onUpdated,
  onMoved,
  onDeleted,
  onError,
}) => {
  const [busyAction, setBusyAction] = useState(null);
  const [targetForumId, setTargetForumId] = useState('');

  const runAction = async (actionKey, action) => {
    if (busyAction) return;
    setBusyAction(actionKey);
    try {
      await action();
    } catch (err) {
      onError?.(err.response?.data?.error || 'Erreur lors de l\'action de modération');
    } finally {
      setBusyAction(null);
    }
  };

  const handleModeration = (payload) => runAction(
    JSON.stringify(payload),
    async () => {
      const result = await threadService.updateModeration(thread.threadId, payload);
      if (result?.forumChanged) {
        onMoved?.(thread.threadId);
        return;
      }
      onUpdated?.(thread.threadId, {
        status: result?.moderation?.status ?? thread.status,
        pinned: result?.moderation?.sticky ?? thread.pinned,
        locked: result?.moderation?.locked ?? thread.locked,
      });
    },
  );

  const handleMove = () => {
    const parsedForumId = Number(targetForumId);
    if (!parsedForumId || parsedForumId === Number(currentForumId)) return;
    return handleModeration({ forumId: parsedForumId });
  };

  const handleDelete = () => {
    if (!window.confirm(`Supprimer définitivement « ${thread.title} » ?`)) return;
    return runAction('delete', async () => {
      await threadService.deleteThread(thread.threadId);
      onDeleted?.(thread.threadId);
    });
  };

  const isClosed = thread.status === 'closed' || thread.locked;
  const isArchived = thread.status === 'archived';
  const moveOptions = forumOptions.filter((option) => Number(option.id) !== Number(currentForumId));

  return (
    <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
      <div className={styles.section}>
        <span className={styles.label}>Modération</span>
        <div className={styles.actions} role="group" aria-label="Actions de modération">
          {isClosed ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSuccess}`}
              disabled={Boolean(busyAction)}
              onClick={() => handleModeration({ status: 'open' })}
            >
              Rouvrir
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnWarning}`}
              disabled={Boolean(busyAction)}
              onClick={() => handleModeration({ status: 'closed' })}
            >
              Fermer
            </button>
          )}
          {!isArchived ? (
            <button
              type="button"
              className={styles.btn}
              disabled={Boolean(busyAction)}
              onClick={() => handleModeration({ status: 'archived' })}
            >
              Archiver
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnActive}`}
              disabled={Boolean(busyAction)}
              onClick={() => handleModeration({ status: 'open' })}
            >
              Désarchiver
            </button>
          )}
          <button
            type="button"
            className={`${styles.btn} ${thread.pinned ? styles.btnActive : ''}`}
            disabled={Boolean(busyAction)}
            onClick={() => handleModeration({ sticky: !thread.pinned })}
          >
            {thread.pinned ? 'Désépingler' : 'Épingler'}
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnDanger}`}
            disabled={Boolean(busyAction)}
            onClick={handleDelete}
          >
            Supprimer
          </button>
        </div>
      </div>

      {moveOptions.length > 0 && (
        <div className={styles.section}>
          <span className={styles.label}>Déplacer vers</span>
          <div className={styles.moveRow}>
            <select
              className={styles.moveSelect}
              value={targetForumId}
              onChange={(event) => setTargetForumId(event.target.value)}
              disabled={Boolean(busyAction)}
            >
              <option value="">Choisir un forum…</option>
              {moveOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.btn}
              disabled={Boolean(busyAction) || !targetForumId}
              onClick={handleMove}
            >
              Déplacer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumThreadModerationActions;
